import { Badge } from "@/components/ui/badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import cacheKeys from "@/config/cache-keys";
import { languageOptions } from "@/config/language-options";
import { LanguageOptionLabel } from "@/components/shared/LanguageFlag";
import AppService from "@/services/AppService";
import {
	UNSET_DOCUMENT_LANGUAGE,
	type Document as DocumentType,
	type DocumentLanguage,
} from "@/types/document/document";
import type { Group } from "@/types/group/group";
import { useQueryClient } from "@tanstack/react-query";
import { FileIcon, TrashIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "sonner";
import TableHeadSortButton from "@/components/admin/TableHeadSortButton";

const appService = new AppService();

type SortState = {
	field: "name" | "category" | "language" | "groups" | "url";
	direction: "asc" | "desc" | "none";
};

interface DocumentsTableProps {
	documents: DocumentType[];
	groups: Group[];
}

export const DocumentsTable = ({ documents, groups }: DocumentsTableProps) => {
	const { t } = useTranslation();
	const [, setFileToDelete] = useState<DocumentType | null>(null);

	const [sortState, setSortState] = useState<SortState>({
		field: "name",
		direction: "asc",
	});

	const SortedDocuments = useMemo(() => {
		const sorted = documents.sort((a, b) => {
			if (sortState.field === "name") {
				return sortState.direction === "asc"
					? a.name.localeCompare(b.name)
					: b.name.localeCompare(a.name);
			} else if (sortState.field === "category") {
				return sortState.direction === "asc"
					? (a.category?.length || 0) - (b.category?.length || 0)
					: (b.category?.length || 0) - (a.category?.length || 0);
			} else if (sortState.field === "language") {
				return sortState.direction === "asc"
					? (a.language ?? "").localeCompare(b.language ?? "")
					: (b.language ?? "").localeCompare(a.language ?? "");
			} else if (sortState.field === "groups") {
				return sortState.direction === "asc"
					? (a.groups?.length || 0) - (b.groups?.length || 0)
					: (b.groups?.length || 0) - (a.groups?.length || 0);
			}
			return 0;
		});
		return sorted;
	}, [sortState, documents]);
	// const handleDelete = () => {};

	return (
		<Table className="table-fixed w-full" aria-label="Dokumenter">
			<TableHeader>
				<TableRow className="bg-moss-green/5 border-b">
					<TableHead className="py-2 px-4 text-xs w-[22%]">
						{t("admin.table.name")}
						<TableHeadSortButton
							label=""
							sortDir={
								sortState.field === "name" ? sortState.direction : "none"
							}
							setSortDir={(dir) =>
								setSortState({ field: "name", direction: dir })
							}
						/>
					</TableHead>
					<TableHead className="text-xs w-[18%]">
						{t("admin.table.category")}
						<TableHeadSortButton
							label=""
							sortDir={
								sortState.field === "category" ? sortState.direction : "none"
							}
							setSortDir={(dir) =>
								setSortState({ field: "category", direction: dir })
							}
						/>
					</TableHead>
					<TableHead className=" text-xs w-[22%]">
						{t("admin.table.group")}
						<TableHeadSortButton
							label=""
							sortDir={
								sortState.field === "groups" ? sortState.direction : "none"
							}
							setSortDir={(dir) =>
								setSortState({ field: "groups", direction: dir })
							}
						/>
					</TableHead>
					<TableHead className="text-xs w-[14%]">
						{t("admin.table.language")}
						<TableHeadSortButton
							label=""
							sortDir={
								sortState.field === "language" ? sortState.direction : "none"
							}
							setSortDir={(dir) =>
								setSortState({ field: "language", direction: dir })
							}
						/>
					</TableHead>
					<TableHead className="py-2 px-4 text-xs w-[24%]">
						{t("admin.table.actions")}
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{SortedDocuments.length === 0 && (
					<TableRow>
						<TableCell
							colSpan={5}
							className="text-center text-muted-foreground"
						>
							{t("admin.table.message.no-documents")}
						</TableCell>
					</TableRow>
				)}
				{SortedDocuments.map((document) => (
					<DocumentsTableRow
						doc={document}
						groups={groups}
						setFileToDelete={setFileToDelete}
						key={document.id}
					/>
				))}
			</TableBody>
		</Table>
	);
};

interface DocumentsTableRowProps {
	doc: DocumentType;
	groups: Group[];
	setFileToDelete: (doc: DocumentType) => void;
}

const DocumentsTableRow = ({ doc, groups }: DocumentsTableRowProps) => {
	const { t } = useTranslation();

	const { accountId } = useParams();
	const queryClient = useQueryClient();
	const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);

	const groupsForDoc = groups
		.filter((group) => (doc.groups ?? []).includes(group.id))
		.map((group) => ({
			id: group.id,
			name: group.name,
		}));

	const updateLanguage = async (language: DocumentLanguage | null) => {
		if (language === (doc.language ?? null)) return;
		if (!accountId) return toast.error("Ingen konto funnet.");

		try {
			setIsUpdatingLanguage(true);
			const promise = appService.updateDocumentLanguage(doc.id, language);

			toast.promise(promise, {
				loading: t("admin.upload_messages.updating_language"),
				success: t("admin.upload_messages.language_updated_success"),
				error: t("admin.upload_messages.language_update_error"),
			});

			await promise;

			queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_ACCOUNT(accountId),
			});
		} catch (error) {
			console.error("Error updating document language:", error);
		} finally {
			setIsUpdatingLanguage(false);
		}
	};

	const deleteDocument = async () => {
		if (!accountId) return toast.error("Ingen konto funnet.");
		try {
			const promise = appService.deleteDocument(doc.id);

			toast.promise(promise, {
				loading: "Sletter dokumentet...",
				success: "Dokumentet ble slettet",
				error: "Noe gikk galt under sletting av Dokumentet.",
			});

			await promise;

			queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_ACCOUNT(accountId),
			});
		} catch (error) {
			console.error("Error deleting document:", error);
		}
	};

	const getDocumentBySasToken = async () => {
		if (!accountId) return toast.error("Ingen dokumenter funnet.");

		try {
			const promise = appService.getDocumentBySasToken(doc.id);

			toast.promise(promise, {
				loading: "Laster ned dokumentet...",
				success: "Dokumentet lastet ned!",
				error: "Noe gikk galt ved henting av dokumentet.",
			});

			const response = await promise;

			const sasUrl = response.Result;

			if (sasUrl) {
				const link = document.createElement("a");
				link.href = sasUrl;
				link.target = "_blank";
				link.rel = "noopener noreferrer";
				link.download = doc.name;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				toast.error("Ugyldig nedlastingslenke");
			}

			queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_ACCOUNT(accountId),
			});
		} catch (error) {
			console.error("Error downloading document:", error);
			toast.error("Noe gikk galt ved nedlasting.");
		}
	};

	return (
		<TableRow>
			<TableCell className="text-xs truncate">
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="truncate block max-w-full">{doc.name}</span>
					</TooltipTrigger>
					<TooltipContent side="bottom" className="max-w-[300px]">
						{doc.name}
					</TooltipContent>
				</Tooltip>
			</TableCell>
			<TableCell className="text-xs">
				{t(`calculator.documents.categories.${doc.category}`)}
			</TableCell>
			<TableCell className="py-2 px-4">
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="flex flex-wrap gap-1">
							{groupsForDoc.length > 0 ? (
								<>
									{groupsForDoc.slice(0, 1).map((group) => (
										<Badge
											key={group.id}
											variant="secondary"
											className="text-xs py-0.5 px-1"
										>
											{group.name}
										</Badge>
									))}
									{groupsForDoc.length > 1 && (
										<Badge variant="outline" className="text-xs py-0.5 px-1">
											+{groupsForDoc.length - 1}
										</Badge>
									)}
								</>
							) : (
								<span className="text-xs">N/A</span>
							)}
						</div>
					</TooltipTrigger>
					{groupsForDoc.length > 0 && (
						<TooltipContent side="bottom" className="max-w-[200px]">
							<p className="text-xs">{t("admin.table.connected-groups")}</p>
							<ul className="list-disc pl-4 text-xs ">
								{groupsForDoc.map((group) => (
									<li key={group.id}>{group.name}</li>
								))}
							</ul>
						</TooltipContent>
					)}
				</Tooltip>
			</TableCell>
			<TableCell className="text-xs pr-2">
				<Select
					value={doc.language ?? UNSET_DOCUMENT_LANGUAGE}
					onValueChange={(value) =>
						updateLanguage(
							value === UNSET_DOCUMENT_LANGUAGE
								? null
								: (value as DocumentLanguage)
						)
					}
					disabled={isUpdatingLanguage}
				>
					<SelectTrigger
						className="h-7 px-2 text-xs [&>span]:inline-flex [&>span]:items-center"
						aria-label={t("admin.table.change-language")}
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem
							value={UNSET_DOCUMENT_LANGUAGE}
							className="text-xs text-muted-foreground hover:bg-soft-white justify-start"
						>
							{t("admin.table.language-unset")}
						</SelectItem>
						{languageOptions.map((option) => (
							<SelectItem
								key={option.value}
								value={option.value}
								className="text-xs hover:bg-soft-white justify-start"
							>
								<LanguageOptionLabel
									language={option.value}
									label={option.iso6392}
								/>
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</TableCell>
			<TableCell className="py-2 px-4">
				<div className="flex items-center gap-2">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								size="sm"
								variant="secondary"
								className="h-7 px-2 text-xs"
								onClick={getDocumentBySasToken}
								aria-label={`Åpne dokument ${doc.name}`}
							>
								<FileIcon className="h-3 w-3 mr-1" />
								{t("admin.button.open")}
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom" className="">
							{t("admin.table.doc-open")} {doc.name}
						</TooltipContent>
					</Tooltip>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button
								size="sm"
								variant="destructive"
								className="h-7 px-2 text-xs"
								aria-label={`Slett dokument ${doc.name}`}
							>
								<TrashIcon className="h-3 w-3" />
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t("admin.dialog.alert.doc-alert-2")}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t("admin.dialog.alert.doc-alert")} <b>{doc.name}</b>?
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>
									{t("admin.button.cancel")}
								</AlertDialogCancel>
								<AlertDialogAction
									onClick={deleteDocument}
									className="bg-destructive hover:bg-destructive/80"
								>
									{t("admin.button.delete")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</TableCell>
		</TableRow>
	);
};
